import axios from 'axios';
import api from './api';

// Cache: { [providerSlug]: { cities: [], timestamp: Date } }
const citiesCacheByProvider = {};
const fetchPromises = {};

/**
 * Fetch fresh cities for a specific provider from our backend
 */
const fetchAndCacheCities = async (providerSlug, credentials = null) => {
    if (!providerSlug) return [];
    
    // Return existing promise if already fetching for this provider
    // Skip cache if we are using custom testing credentials
    if (!credentials && fetchPromises[providerSlug]) return fetchPromises[providerSlug];

    const fetchPromise = (async () => {
        try {
            const config = {};
            if (credentials) {
                config.headers = { 'x-provider-auth': JSON.stringify(credentials) };
            }
            const response = await api.get(`/delivery-companies/${providerSlug}/cities`, config);
            const cities = response.data?.data || [];

            const sorted = cities.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
            
            // Only cache if we are NOT using temporary testing credentials
            if (!credentials) {
                citiesCacheByProvider[providerSlug] = {
                    cities: sorted,
                    timestamp: Date.now()
                };
            }

            return sorted;
        } catch (error) {
            console.warn(`[citiesService] ⚠️ Failed to fetch cities for ${providerSlug}:`, error.message);
            return citiesCacheByProvider[providerSlug]?.cities || [];
        } finally {
            if (!credentials) {
                delete fetchPromises[providerSlug];
            }
        }
    })();

    if (!credentials) fetchPromises[providerSlug] = fetchPromise;
    return fetchPromise;
};

/**
 * Get all cities for a specific delivery provider (SYNC/ASYNC mix)
 * Returns cached data immediately, triggers fetch if empty
 * @param {string} providerSlug 
 * @returns {Array} Array of city objects { id, name }
 */
export const getCitiesByProvider = (providerSlug) => {
    if (!providerSlug) return [];
    
    const entry = citiesCacheByProvider[providerSlug];
    
    // If no cache or older than 1 hour, trigger a background refresh
    const isStale = !entry || (Date.now() - entry.timestamp > 60 * 60 * 1000);
    if (isStale) {
        fetchAndCacheCities(providerSlug);
    }

    return entry?.cities || [];
};

/**
 * Explicitly wait for cities to load for a provider
 * @param {string} providerSlug 
 * @returns {Promise<Array>}
 */
export const ensureCitiesLoaded = async (providerSlug, credentials = null) => {
    if (!providerSlug) return [];
    
    // Always bypass cache if verifying credentials dynamically
    if (credentials) {
        return await fetchAndCacheCities(providerSlug, credentials);
    }
    
    // Cache logic for normal loads
    const entry = citiesCacheByProvider[providerSlug];
    if (entry && entry.cities.length > 0) {
        const age = Date.now() - entry.timestamp;
        if (age < 15 * 60 * 1000) {
            return entry.cities;
        }
    }
    return await fetchAndCacheCities(providerSlug);
};

/**
 * Force refresh cities from API
 * @param {string} providerSlug
 * @returns {Promise<void>}
 */
export const refreshCities = (providerSlug) => fetchAndCacheCities(providerSlug);

/**
 * Find a city object by its ID for a specific provider
 */
export const findCityById = (providerSlug, cityId) => {
    const cities = getCitiesByProvider(providerSlug);
    return cities.find(c => String(c.id) === String(cityId)) || null;
};

/**
 * Find a city object by its name for a specific provider
 */
export const findCityByName = (providerSlug, cityName) => {
    const cities = getCitiesByProvider(providerSlug);
    if (!cityName) return null;
    const normalizedSearch = cityName.toLowerCase().trim();
    return cities.find(c => (c.name || '').toLowerCase().trim() === normalizedSearch) || null;
};
